import { Promotion } from 'reducers/playerPromos';

import { normalizeRules } from './promoDisplay';

// Promo weeks roll over at midnight Central time on the promo's reset_day — the
// same boundary the server uses to pick the current week, regardless of the
// player's own timezone.
const PROMO_TIME_ZONE = 'America/Chicago';
const DEFAULT_RESET_DAY = 6; // Saturday, matching the server default
const DAY_MS = 24 * 60 * 60 * 1000;

// EN-11002: nothing on the server pushes promo data at the reset, so the client
// fetches it itself one minute after the boundary (product decision).
export const PROMO_RESET_REFRESH_DELAY_MS = 60 * 1000;
// Random extra delay per session, so every open client does not hit
// getPlayerPromos in the same second.
export const PROMO_RESET_REFRESH_JITTER_MS = 30 * 1000;

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6
};

const centralTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: PROMO_TIME_ZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  weekday: 'short',
  hour: 'numeric',
  minute: 'numeric',
  hourCycle: 'h23'
});

const getCentralTimeParts = (epochMs: number) => {
  const parts = Object.fromEntries(
    centralTimeFormatter.formatToParts(new Date(epochMs)).map((part) => [part.type, part.value])
  );

  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    weekday: WEEKDAY_INDEX[parts.weekday],
    hour: Number(parts.hour),
    minute: Number(parts.minute)
  };
};

// Epoch ms of 00:00 Central time on the given calendar date. Central DST changes
// at 02:00, so the offset sampled at the preceding UTC midnight is the offset
// in effect at local midnight.
const getCentralMidnight = (year: number, month: number, day: number): number => {
  const utcMidnight = Date.UTC(year, month - 1, day);
  const central = getCentralTimeParts(utcMidnight);
  const centralAsUtc = Date.UTC(
    central.year,
    central.month - 1,
    central.day,
    central.hour,
    central.minute
  );

  return utcMidnight + (utcMidnight - centralAsUtc);
};

const normalizeResetDay = (value: unknown): number => {
  const resetDay = Number(value ?? DEFAULT_RESET_DAY);
  return Number.isInteger(resetDay) && resetDay >= 0 && resetDay <= 6
    ? resetDay
    : DEFAULT_RESET_DAY;
};

/** Distinct weekly reset days (0 = Sunday … 6 = Saturday) of the active promos, ascending. */
export const getActivePromoResetDays = (promotions: Promotion[] = []): number[] =>
  Array.from(
    new Set(
      promotions
        .filter((promo) => promo?.is_active)
        .map((promo) => normalizeResetDay(normalizeRules(promo).reset_day))
    )
  ).sort((a, b) => a - b);

const getRefreshAtDaysFrom = (
  today: ReturnType<typeof getCentralTimeParts>,
  days: number
): number => {
  const resetDate = new Date(Date.UTC(today.year, today.month - 1, today.day) + days * DAY_MS);
  const resetAt = getCentralMidnight(
    resetDate.getUTCFullYear(),
    resetDate.getUTCMonth() + 1,
    resetDate.getUTCDate()
  );

  return resetAt + PROMO_RESET_REFRESH_DELAY_MS;
};

/** Epoch ms of the next refetch for one reset day: one minute after its weekly reset, after `nowMs`. */
const getNextRefreshAtForResetDay = (resetDay: number, nowMs: number): number => {
  const today = getCentralTimeParts(nowMs);
  const daysUntilReset = (resetDay - today.weekday + 7) % 7;
  const refreshAt = getRefreshAtDaysFrom(today, daysUntilReset);

  return refreshAt > nowMs ? refreshAt : getRefreshAtDaysFrom(today, daysUntilReset + 7);
};

/** Epoch ms at which to refetch promos — one minute after the soonest upcoming reset — or null. */
export const getNextPromoRefreshAt = (resetDays: number[], nowMs: number): number | null => {
  if (resetDays.length === 0) return null;

  return Math.min(...resetDays.map((resetDay) => getNextRefreshAtForResetDay(resetDay, nowMs)));
};
